/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { Box } from "@material-ui/core";
import { useEmoBalance } from "hooks/paw/useEmoBalance";
import React from "react";
import { useSelector } from "store";

import { StreamingActionHeader } from "./StreamingActionHeader";
import { StreamingAnnouncement } from "./StreamingAnnouncement";
import StreamingCommentV2 from "./StreamingCommentV2";
import { StreamingSelectGiftV2 } from "./StreamingSelectGiftV2";
import { StreamingSendCommentV2 } from "./StreamingSendCommentV2";
import { useSendComment } from "./useSendComment";

import type { ActionAreaType } from "./useActionAreaType";
import type { Timestamp } from "@google-cloud/firestore";
import type { THEOPlayer } from "@spwn/types/external";
import type { streamingState, CommentFilterType } from "modules/streaming";
import type { FC } from "react";
import type { Store } from "store";
import type { EventInfo } from "utility";

type Props = {
  player: THEOPlayer;
  eventStart: Timestamp;
  eventInfo: EventInfo;
  giftItemMap: streamingState["giftItemMap"];
  currentVideoId: string;
  THEOPlayerElementHeight: number;
  isDarkModeEnabled: boolean;
  isOpenAnnouncement: boolean;
  hasDvrStartTime: boolean;
  handleToggleAnnouncement: () => void;
  isEditingAnnouncement: boolean;
  setIsEditingAnnouncement: (isEditing: boolean) => void;
  streamingAnnouncement: string;
  setStreamingAnnouncement: (announcement: string) => void;
  actionAreaType: ActionAreaType;
  selectedCommentFilter: CommentFilterType;
  handleChangeCommentFilter: (filter: CommentFilterType) => void;
  handleCloseActionArea: () => void;
};

/**
 * UserActionArea
 * comment, gift, (later: goods sales, quiz, vote)
 * @param props
 */
const StreamingActionAreaV2Comment: React.FC<Props> = (props) => {
  // ----- Hooks -----
  const { paidValue: emoBalance } = useEmoBalance();
  const {
    commentInputRef,
    iconUrls,
    comment,
    giftItems,
    selectedGiftItem,
    canSendComment,
    canPurchaseGiftItem,
    isOpenGiftDrawer,
    handleSetComment,
    handleSendComment,
    handleOpenGiftDrawer,
    handleCloseGiftDrawer,
    handleClickSelectedGift,
    handleOpenPawChargeModal,
  } = useSendComment({
    currentVideoId: props.currentVideoId,
  });

  // ----- Redux State -----
  const isDarkModeEnabled = useSelector(
    (state) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const user = useSelector((state: Store) => state.auth.user);
  const streamingType = useSelector((state) => state.streaming.streamingType);

  return (
    <StreamingActionAreaLayout
      header={
        <StreamingActionHeader
          isDarkModeEnabled={isDarkModeEnabled}
          selectedCommentFilter={props.selectedCommentFilter}
          handleChangeCommentFilter={props.handleChangeCommentFilter}
          handleToggleAnnouncement={props.handleToggleAnnouncement}
          handleCloseActionArea={props.handleCloseActionArea}
          showAnnouncement={isAdmin || props.streamingAnnouncement !== ""}
        />
      }
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
    >
      <StreamingAnnouncement
        eventId={props.eventInfo.eventId}
        currentVideoId={props.currentVideoId}
        streamingType={streamingType}
        isDarkModeEnabled={isDarkModeEnabled}
        isAdmin={isAdmin}
        isOpenAnnouncement={props.isOpenAnnouncement}
        isEditingAnnouncement={props.isEditingAnnouncement}
        setIsEditingAnnouncement={props.setIsEditingAnnouncement}
        streamingAnnouncement={props.streamingAnnouncement}
        setStreamingAnnouncement={props.setStreamingAnnouncement}
      />

      {streamingType === "Live" && user.uid && (
        <StreamingSendCommentV2
          isDarkModeEnabled={isDarkModeEnabled}
          isOpenGiftDrawer={isOpenGiftDrawer}
          isSelectedGift={selectedGiftItem !== null}
          canPurchaseGiftItem={canPurchaseGiftItem}
          userIcon={iconUrls?.["32"]}
          userName={user.displayName ?? ""}
          inputRef={commentInputRef}
          comment={comment}
          canSendComment={canSendComment}
          giftItems={giftItems}
          onClickSendComment={handleSendComment}
          setComment={handleSetComment}
          onClickOpenGiftDrawer={handleOpenGiftDrawer}
          onClickOpenPawChargeModal={handleOpenPawChargeModal}
        />
      )}

      {isOpenGiftDrawer && giftItems.length > 0 && (
        <StreamingSelectGiftV2
          isDarkModeEnabled={isDarkModeEnabled}
          canPurchaseGiftItem={canPurchaseGiftItem}
          iconUrls={iconUrls?.["32"]}
          userName={user.displayName ?? ""}
          comment={comment}
          emoBalance={emoBalance}
          giftItems={giftItems}
          selectedGiftItem={selectedGiftItem}
          canSendComment={canSendComment}
          onClickSendComment={handleSendComment}
          onClickCloseGiftDrawer={handleCloseGiftDrawer}
          onClickSelectedGift={handleClickSelectedGift}
          onClickOpenPawChargeModal={handleOpenPawChargeModal}
        />
      )}

      <Box flex={"1 1 auto"}>
        <StreamingCommentV2
          player={props.player}
          eventStart={props.eventStart}
          streamingType={streamingType}
          eventInfo={props.eventInfo}
          giftItemMap={props.giftItemMap}
          currentVideoId={props.currentVideoId}
          selectedCommentFilter={props.selectedCommentFilter}
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
          hasDvrStartTime={props.hasDvrStartTime}
        />
      </Box>
    </StreamingActionAreaLayout>
  );
};
export default StreamingActionAreaV2Comment;

const StreamingActionAreaLayout: FC<{
  children: React.ReactNode;
  header: React.ReactNode;
  THEOPlayerElementHeight: number;
}> = ({ children, header, THEOPlayerElementHeight }) => {
  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gridGap={"12px"}
      padding={"12px"}
      minHeight={`${THEOPlayerElementHeight}px`}
      css={css`
        height: calc(100vh - ${THEOPlayerElementHeight}px); /* fallback */
        @supports (height: 100dvh) {
          height: calc(100dvh - ${THEOPlayerElementHeight}px);
        }
      `}
    >
      {header}
      {children}
    </Box>
  );
};
